import { useState, useEffect, useRef } from 'react'
import { getFirestore, collection, doc, onSnapshot, addDoc, updateDoc, deleteDoc, serverTimestamp, query, where, orderBy } from 'firebase/firestore'
import { auth } from '@/lib/firebase'

interface Lead {
  id: string
  name: string
  email?: string
  phone?: string
  source?: string
  notes?: string
  columnId: string
  order: number
  createdAt?: any
}

interface Column {
  id: string
  title: string
  color: string
  order: number
}

const DEFAULT_COLUMNS: Omit<Column, 'id'>[] = [
  { title: 'Novo',        color: '#6366f1', order: 0  },
  { title: 'Contactado',  color: '#3b82f6', order: 1  },
  { title: 'Qualificado', color: '#8b5cf6', order: 2  },
  { title: 'Proposta',    color: '#f59e0b', order: 3  },
  { title: 'Follow-up 1', color: '#10b981', order: 4  },
  { title: 'Follow-up 2', color: '#10b981', order: 5  },
  { title: 'Follow-up 3', color: '#10b981', order: 6  },
  { title: 'Follow-up 4', color: '#10b981', order: 7  },
  { title: 'Follow-up 5', color: '#10b981', order: 8  },
  { title: 'Follow-up 6', color: '#10b981', order: 9  },
  { title: 'Fechado',     color: '#22c55e', order: 10 },
  { title: 'Perdido',     color: '#ef4444', order: 11 },
]

const COLORS = ['#6366f1','#3b82f6','#8b5cf6','#f59e0b','#10b981','#22c55e','#ef4444','#ec4899','#f97316','#06b6d4']

export default function CRMPage() {
  const uid = auth.currentUser?.uid || 'dev-user'
  const db = getFirestore()

  const [columns, setColumns] = useState<Column[]>([])
  const [leads, setLeads] = useState<Lead[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedLead, setExpandedLead] = useState<string | null>(null)

  const [colModal, setColModal] = useState<{ open: boolean; col?: Column }>({ open: false })
  const [leadModal, setLeadModal] = useState<{ open: boolean; lead?: Lead; columnId?: string }>({ open: false })
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; type: 'col'|'lead'; id: string; title: string }>({ open: false, type: 'col', id: '', title: '' })

  const dragCard = useRef<{ leadId: string; fromCol: string } | null>(null)
  const dragCol = useRef<string | null>(null)

  const [colForm, setColForm] = useState({ title: '', color: '#6366f1' })
  const [leadForm, setLeadForm] = useState({ name: '', email: '', phone: '', source: '', notes: '' })

  useEffect(() => {
    const colRef = collection(db, 'crm_columns')
    const q = query(colRef, where('uid', '==', uid), orderBy('order'))
    const unsub = onSnapshot(q, async snap => {
      if (snap.empty) {
        for (const col of DEFAULT_COLUMNS) {
          await addDoc(colRef, { ...col, uid })
        }
        return
      }
      setColumns(snap.docs.map(d => ({ id: d.id, ...d.data() } as Column)))
      setLoading(false)
    })
    return unsub
  }, [uid])

  useEffect(() => {
    const leadsRef = collection(db, 'crm_leads')
    const q = query(leadsRef, where('uid', '==', uid), orderBy('order'))
    const unsub = onSnapshot(q, snap => {
      setLeads(snap.docs.map(d => ({ id: d.id, ...d.data() } as Lead)))
    })
    return unsub
  }, [uid])

  async function saveColumn() {
    if (!colForm.title.trim()) return
    if (colModal.col) {
      await updateDoc(doc(db, 'crm_columns', colModal.col.id), { title: colForm.title, color: colForm.color })
    } else {
      const maxOrder = columns.reduce((m, c) => Math.max(m, c.order), -1)
      await addDoc(collection(db, 'crm_columns'), { uid, title: colForm.title, color: colForm.color, order: maxOrder + 1 })
    }
    setColModal({ open: false })
  }

  async function deleteColumn(id: string) {
    await deleteDoc(doc(db, 'crm_columns', id))
    const firstCol = columns.find(c => c.order === 0)
    if (firstCol) {
      const affected = leads.filter(l => l.columnId === id)
      for (const l of affected) {
        await updateDoc(doc(db, 'crm_leads', l.id), { columnId: firstCol.id })
      }
    }
  }

  async function saveLead() {
    if (!leadForm.name.trim()) return
    const colId = leadModal.lead?.columnId || leadModal.columnId || columns[0]?.id
    if (leadModal.lead) {
      await updateDoc(doc(db, 'crm_leads', leadModal.lead.id), {
        name: leadForm.name, email: leadForm.email,
        phone: leadForm.phone, source: leadForm.source, notes: leadForm.notes,
      })
    } else {
      const maxOrder = leads.filter(l => l.columnId === colId).reduce((m, l) => Math.max(m, l.order), -1)
      await addDoc(collection(db, 'crm_leads'), {
        uid, columnId: colId, name: leadForm.name, email: leadForm.email,
        phone: leadForm.phone, source: leadForm.source, notes: leadForm.notes,
        order: maxOrder + 1, createdAt: serverTimestamp(),
      })
    }
    setLeadModal({ open: false })
  }

  async function moveLead(leadId: string, toColId: string) {
    const maxOrder = leads.filter(l => l.columnId === toColId).reduce((m, l) => Math.max(m, l.order), -1)
    await updateDoc(doc(db, 'crm_leads', leadId), { columnId: toColId, order: maxOrder + 1 })
  }

  async function deleteLead(id: string) {
    await deleteDoc(doc(db, 'crm_leads', id))
  }

  function onDragStartCard(leadId: string, fromCol: string) {
    dragCard.current = { leadId, fromCol }
  }

  async function onDropCard(toColId: string) {
    if (!dragCard.current) return
    await moveLead(dragCard.current.leadId, toColId)
    dragCard.current = null
  }

  function onDragStartCol(colId: string) {
    dragCol.current = colId
  }

  async function onDropCol(toColId: string) {
    if (!dragCol.current || dragCol.current === toColId) return
    const fromCol = columns.find(c => c.id === dragCol.current)
    const toCol   = columns.find(c => c.id === toColId)
    if (!fromCol || !toCol) return
    await updateDoc(doc(db, 'crm_columns', fromCol.id), { order: toCol.order })
    await updateDoc(doc(db, 'crm_columns', toCol.id),   { order: fromCol.order })
    dragCol.current = null
  }

  function openNewCol() { setColForm({ title: '', color: '#6366f1' }); setColModal({ open: true }) }
  function openEditCol(col: Column) { setColForm({ title: col.title, color: col.color }); setColModal({ open: true, col }) }
  function openNewLead(columnId: string) { setLeadForm({ name: '', email: '', phone: '', source: '', notes: '' }); setLeadModal({ open: true, columnId }) }
  function openEditLead(lead: Lead) { setLeadForm({ name: lead.name, email: lead.email || '', phone: lead.phone || '', source: lead.source || '', notes: lead.notes || '' }); setLeadModal({ open: true, lead }) }

  const sortedColumns = [...columns].sort((a, b) => a.order - b.order)

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <p className="text-gray-400">Carregando CRM...</p>
    </div>
  )

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-6 flex-shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-white">CRM</h1>
          <p className="text-gray-400 text-sm">{leads.length} leads no pipeline</p>
        </div>
        <div className="flex gap-3">
          <button onClick={openNewCol}
            className="px-4 py-2 rounded-xl text-sm font-medium border border-white/10 text-gray-300 hover:text-white hover:border-white/30 transition-all">
            + Coluna
          </button>
          <button onClick={() => openNewLead(columns[0]?.id)}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all"
            style={{background:'linear-gradient(135deg,#6366f1,#10b981)'}}>
            + Novo Lead
          </button>
        </div>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4 flex-1">
        {sortedColumns.map(col => {
          const colLeads = leads.filter(l => l.columnId === col.id).sort((a,b) => a.order - b.order)
          return (
            <div key={col.id}
              className="flex-shrink-0 w-72 flex flex-col rounded-2xl bg-white/5 border border-white/10"
              draggable
              onDragStart={() => onDragStartCol(col.id)}
              onDragOver={e => e.preventDefault()}
              onDrop={() => onDropCol(col.id)}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{background: col.color}} />
                  <span className="text-white font-medium text-sm truncate">{col.title}</span>
                  <span className="text-xs text-gray-500 ml-1">{colLeads.length}</span>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEditCol(col)} className="p-1 text-gray-500 hover:text-white transition-colors text-xs">✏️</button>
                  <button onClick={() => setDeleteConfirm({ open: true, type: 'col', id: col.id, title: col.title })} className="p-1 text-gray-500 hover:text-red-400 transition-colors text-xs">🗑</button>
                </div>
              </div>

              <div className="flex-1 p-2 space-y-2 min-h-16 overflow-y-auto"
                onDragOver={e => e.preventDefault()}
                onDrop={() => onDropCard(col.id)}
              >
                {colLeads.map(lead => {
                  const isExpanded = expandedLead === lead.id
                  return (
                    <div key={lead.id}
                      draggable
                      onDragStart={() => onDragStartCard(lead.id, col.id)}
                      className="bg-white/8 border border-white/10 rounded-xl p-3 cursor-grab active:cursor-grabbing hover:border-white/25 transition-all group"
                    >
                      {/* Card header */}
                      <div className="flex items-start justify-between gap-1">
                        <button onClick={() => setExpandedLead(isExpanded ? null : lead.id)}
                          className="text-white text-sm font-medium truncate text-left hover:text-indigo-300 transition-colors">
                          {lead.name}
                        </button>
                        <div className="flex gap-1 flex-shrink-0">
                          <button onClick={() => openEditLead(lead)} className="text-gray-400 hover:text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">✏️</button>
                          <button onClick={() => setDeleteConfirm({ open: true, type: 'lead', id: lead.id, title: lead.name })} className="text-gray-400 hover:text-red-400 text-xs opacity-0 group-hover:opacity-100 transition-opacity">🗑</button>
                          <button onClick={() => setExpandedLead(isExpanded ? null : lead.id)}
                            className="text-gray-400 hover:text-white text-xs transition-colors">
                            {isExpanded ? '▲' : '▼'}
                          </button>
                        </div>
                      </div>

                      {lead.email && <p className="text-gray-400 text-xs mt-1 truncate">{lead.email}</p>}
                      {lead.phone && <p className="text-gray-400 text-xs truncate">{lead.phone}</p>}

                      {lead.source && (
                        <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded-full bg-white/10 text-gray-300">
                          {lead.source}
                        </span>
                      )}

                      {/* Expanded content */}
                      {isExpanded && (
                        <div className="mt-3 pt-3 border-t border-white/10 space-y-3">
                          {lead.notes && (
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Notas</p>
                              <p className="text-gray-300 text-xs whitespace-pre-wrap">{lead.notes}</p>
                            </div>
                          )}
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Mover para</p>
                            <select
                              value={col.id}
                              onChange={e => moveLead(lead.id, e.target.value)}
                              onClick={e => e.stopPropagation()}
                              className="w-full bg-white/10 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-500"
                            >
                              {sortedColumns.map(c => (
                                <option key={c.id} value={c.id} className="bg-gray-900">
                                  {c.id === col.id ? `✓ ${c.title}` : c.title}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              <button onClick={() => openNewLead(col.id)}
                className="m-2 py-2 rounded-xl text-xs text-gray-500 hover:text-white hover:bg-white/5 transition-all border border-dashed border-white/10 hover:border-white/20">
                + Lead
              </button>
            </div>
          )
        })}
      </div>

      {/* Modal — Coluna */}
      {colModal.open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-sm">
            <h2 className="text-white font-semibold mb-4">{colModal.col ? 'Editar Coluna' : 'Nova Coluna'}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Nome</label>
                <input value={colForm.title} onChange={e => setColForm({...colForm, title: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:border-indigo-500"
                  placeholder="Ex: Em negociação" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Cor</label>
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map(c => (
                    <button key={c} onClick={() => setColForm({...colForm, color: c})}
                      className="w-7 h-7 rounded-full transition-all"
                      style={{background: c, outline: colForm.color === c ? '3px solid white' : 'none', outlineOffset: '2px'}} />
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setColModal({ open: false })} className="flex-1 py-2.5 rounded-xl border border-white/10 text-gray-300 hover:text-white text-sm">Cancelar</button>
              <button onClick={saveColumn} className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold" style={{background:'linear-gradient(135deg,#6366f1,#10b981)'}}>Salvar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal — Lead */}
      {leadModal.open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-sm">
            <h2 className="text-white font-semibold mb-4">{leadModal.lead ? 'Editar Lead' : 'Novo Lead'}</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Nome *</label>
                <input value={leadForm.name} onChange={e => setLeadForm({...leadForm, name: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:border-indigo-500"
                  placeholder="Nome do lead" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">E-mail</label>
                <input value={leadForm.email} onChange={e => setLeadForm({...leadForm, email: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:border-indigo-500"
                  placeholder="email@exemplo.com" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Telefone</label>
                <input value={leadForm.phone} onChange={e => setLeadForm({...leadForm, phone: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:border-indigo-500"
                  placeholder="(51) 99999-9999" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Origem</label>
                <select value={leadForm.source} onChange={e => setLeadForm({...leadForm, source: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:border-indigo-500">
                  <option value="" className="bg-gray-900">Selecione...</option>
                  <option value="Meta Ads" className="bg-gray-900">Meta Ads</option>
                  <option value="Google Ads" className="bg-gray-900">Google Ads</option>
                  <option value="Orgânico" className="bg-gray-900">Orgânico</option>
                  <option value="WhatsApp" className="bg-gray-900">WhatsApp</option>
                  <option value="Indicação" className="bg-gray-900">Indicação</option>
                  <option value="Landing Page" className="bg-gray-900">Landing Page</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Notas</label>
                <textarea value={leadForm.notes} onChange={e => setLeadForm({...leadForm, notes: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:border-indigo-500 resize-none"
                  rows={3} placeholder="Observações sobre o lead..." />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setLeadModal({ open: false })} className="flex-1 py-2.5 rounded-xl border border-white/10 text-gray-300 hover:text-white text-sm">Cancelar</button>
              <button onClick={saveLead} className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold" style={{background:'linear-gradient(135deg,#6366f1,#10b981)'}}>Salvar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal — Confirmar exclusão */}
      {deleteConfirm.open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-sm">
            <h2 className="text-white font-semibold mb-2">Confirmar exclusão</h2>
            <p className="text-gray-400 text-sm mb-6">
              Tem certeza que deseja remover <span className="text-white font-medium">"{deleteConfirm.title}"</span>?
              {deleteConfirm.type === 'col' && ' Os leads serão movidos para a primeira coluna.'}
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm({ ...deleteConfirm, open: false })} className="flex-1 py-2.5 rounded-xl border border-white/10 text-gray-300 hover:text-white text-sm">Cancelar</button>
              <button onClick={async () => {
                if (deleteConfirm.type === 'col') await deleteColumn(deleteConfirm.id)
                else await deleteLead(deleteConfirm.id)
                setDeleteConfirm({ ...deleteConfirm, open: false })
              }} className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold bg-red-500 hover:bg-red-600 transition-colors">
                Remover
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
